// Sección "Gestión > Accesos a Monitor" en Monitor web.
//
// Permite al dueño administrar los emails autorizados a acceder a Monitor
// (doc `users/{key}/auth`). Espeja la funcionalidad de ConfigMonitor.qml
// en tc_qml, excepto la resincronización (no se expone por ahora).
//
// Gate: mismo flag can_edit_tariffs (flag general de Gestión). El caller
// NO puede modificar sus propios accesos (backend rechaza con SELF_EDIT).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  Spinner,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import { auth, firestore } from "../../firebase";
import { collection, onSnapshot } from "firebase/firestore";

const TCSOCKET_URL =
  process.env.REACT_APP_TCSOCKET_URL || "https://api.ticketcontrol.net";

const EMAIL_RE = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

const AccesosMonitor = ({ currentCompany, canEditPermissions }) => {
  document.title = "Accesos a Monitor | Monitor";
  const machineKey = currentCompany?.id;

  const [items, setItems] = useState([]);
  const [currentEmail, setCurrentEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Form de agregar
  const [newEmail, setNewEmail] = useState("");
  const [newCanEdit, setNewCanEdit] = useState(false);

  const callEndpoint = useCallback(
    async (path, body) => {
      try {
        const user = auth.currentUser;
        if (!user) {
          toast.error("Sesión expirada. Volvé a iniciar sesión.");
          return { ok: false };
        }
        const token = await user.getIdToken();
        const res = await fetch(`${TCSOCKET_URL}/monitor/${path}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ key: machineKey, ...body }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(data.message || "Error en la operación");
          return { ok: false, data };
        }
        return { ok: true, data };
      } catch (err) {
        toast.error(`Error de red: ${err.message || err}`);
        return { ok: false };
      }
    },
    [machineKey]
  );

  // Capturamos el email del usuario actual una sola vez al montar — Firebase
  // Auth ya lo tiene; no necesitamos ir al server para eso.
  useEffect(() => {
    const u = auth.currentUser;
    if (u?.email) setCurrentEmail(u.email.toLowerCase());
  }, []);

  // Subscribe en vivo a users/{machineKey}/auth. Refleja cambios desde
  // cualquier origen (tc_qml local, Monitor web, TCSocket directo) sin
  // tener que pollear.
  useEffect(() => {
    if (!machineKey) return;
    setLoading(true);
    const authCol = collection(firestore, "users", machineKey, "auth");
    const unsubscribe = onSnapshot(
      authCol,
      (snap) => {
        const next = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            email: data.email || "",
            can_edit_tariffs: Boolean(data.can_edit_tariffs),
          };
        });
        setItems(next);
        setLoading(false);
      },
      (err) => {
        console.error("[AccesosMonitor] onSnapshot error:", err);
        toast.error("Error al cargar la lista de accesos");
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [machineKey]);

  const handleAdd = async () => {
    const email = (newEmail || "").trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      toast.error("Email inválido");
      return;
    }
    if (email === currentEmail) {
      toast.info("Ya estás autorizado (no podés agregarte a vos mismo).");
      return;
    }
    setBusy(true);
    const res = await callEndpoint("auth-emails-add", {
      email,
      can_edit_tariffs: newCanEdit,
    });
    setBusy(false);
    if (res.ok) {
      toast.success(
        res.data.updated ? "Permisos actualizados" : "Email agregado"
      );
      setNewEmail("");
      setNewCanEdit(false);
      // La lista se actualiza sola vía onSnapshot.
    }
  };

  const handleToggleGestion = async (row) => {
    if (row.email === currentEmail) return; // safety
    setBusy(true);
    await callEndpoint("auth-emails-update", {
      email: row.email,
      can_edit_tariffs: !row.can_edit_tariffs,
    });
    setBusy(false);
    // onSnapshot refresca la lista.
  };

  const handleRemove = async (row) => {
    if (row.email === currentEmail) return;
    if (!window.confirm(`¿Quitar acceso a Monitor de ${row.email}?`)) return;
    setBusy(true);
    const res = await callEndpoint("auth-emails-remove", {
      email: row.email,
    });
    setBusy(false);
    if (res.ok) {
      toast.success("Email eliminado");
      // onSnapshot refresca la lista.
    }
  };

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const ae = String(a.email || "").toLowerCase();
      const be = String(b.email || "").toLowerCase();
      // Current email primero
      if (ae === currentEmail) return -1;
      if (be === currentEmail) return 1;
      return ae.localeCompare(be);
    });
    return copy;
  }, [items, currentEmail]);

  if (!machineKey) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody className="text-center py-5">
              <span
                className="material-symbols-rounded d-block mb-2"
                style={{ fontSize: 48, color: "#ccc" }}
              >
                group
              </span>
              <h5 className="text-muted mb-0">Seleccioná un cliente primero</h5>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3 align-items-center">
          <Col>
            <h4 className="mb-0">Accesos a Monitor</h4>
            <p className="text-muted small mb-0">
              Gestioná qué emails pueden ingresar a Monitor para supervisar
              este estacionamiento.
            </p>
          </Col>
        </Row>

        {/* Info box sobre permisos de Gestión */}
        <Card
          className="mb-3"
          style={{
            borderLeft: "4px solid #50a5f1",
            backgroundColor: "rgba(80, 165, 241, 0.08)",
          }}
        >
          <CardBody className="py-2 px-3">
            <div className="d-flex align-items-start gap-2">
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 22, color: "#50a5f1" }}
              >
                shield_person
              </span>
              <div className="small" style={{ color: "#1f4e79" }}>
                <b>Permisos de Gestión.</b> Marcá "Gestión" en un email para
                permitirle modificar desde Monitor: tarifas, categorías,
                precios, permisos de operadores e informes. Sin ese permiso,
                el usuario solo puede consultar datos.
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Form: agregar email */}
        {canEditPermissions && (
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-3">
                <span
                  className="material-symbols-rounded align-middle me-1"
                  style={{ fontSize: 18 }}
                >
                  person_add
                </span>
                Agregar email
              </h6>
              <Row className="g-2 align-items-center">
                <Col>
                  <Input
                    type="email"
                    placeholder="ejemplo@email.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                    disabled={busy}
                  />
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    id="newCanEdit"
                    checked={newCanEdit}
                    onChange={(e) => setNewCanEdit(e.target.checked)}
                    disabled={busy}
                    style={{ marginTop: 0 }}
                  />
                  <label htmlFor="newCanEdit" className="small mb-0">
                    Permitir Gestión
                  </label>
                </Col>
                <Col xs="auto">
                  <Button
                    color="primary"
                    onClick={handleAdd}
                    disabled={busy || !newEmail.trim()}
                  >
                    {busy ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <span
                          className="material-symbols-rounded align-middle me-1"
                          style={{ fontSize: 18 }}
                        >
                          add
                        </span>
                        Agregar
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
              <div className="text-muted small mt-2">
                El email debe estar previamente registrado en
                monitor.ticketcontrol.com.ar para que pueda iniciar sesión.
              </div>
            </CardBody>
          </Card>
        )}

        {/* Lista de emails autorizados */}
        <Card>
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0">
                <span
                  className="material-symbols-rounded align-middle me-1"
                  style={{ fontSize: 18 }}
                >
                  group
                </span>
                Emails autorizados
              </h6>
              <Badge color="info" pill>
                {items.length}
              </Badge>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <Spinner size="sm" className="me-2" />
                Cargando...
              </div>
            ) : sortedItems.length === 0 ? (
              <div className="text-muted small fst-italic text-center py-3">
                No hay emails autorizados.
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {sortedItems.map((row) => (
                  <AccessRow
                    key={row.email}
                    row={row}
                    isCurrentUser={row.email === currentEmail}
                    canEdit={!!canEditPermissions}
                    busy={busy}
                    onToggleGestion={() => handleToggleGestion(row)}
                    onRemove={() => handleRemove(row)}
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Row de un email autorizado
// ─────────────────────────────────────────────────────────
const AccessRow = ({ row, isCurrentUser, canEdit, busy, onToggleGestion, onRemove }) => {
  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 8,
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        background: isCurrentUser ? "#f0f9ff" : "#fff",
      }}
    >
      <span
        className="material-symbols-rounded"
        style={{ fontSize: 20, color: isCurrentUser ? "#50a5f1" : "#9aa5b1" }}
      >
        person
      </span>
      <div style={{ flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 8 }}>
        <b style={{ fontSize: 14 }}>{row.email}</b>
        {isCurrentUser && (
          <Badge color="info" pill>
            Vos
          </Badge>
        )}
      </div>

      {/* Toggle Gestión */}
      <div className="d-flex align-items-center gap-2">
        <span
          onClick={() => !isCurrentUser && canEdit && !busy && onToggleGestion()}
          style={{
            cursor: isCurrentUser || !canEdit || busy ? "default" : "pointer",
            padding: "5px 12px",
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            userSelect: "none",
            opacity: isCurrentUser ? 0.6 : 1,
            backgroundColor: row.can_edit_tariffs ? "#50a5f1" : "#f1f5f9",
            color: row.can_edit_tariffs ? "#fff" : "#475569",
            border: `1px solid ${row.can_edit_tariffs ? "#50a5f1" : "#e2e8f0"}`,
            transition: "all 0.12s ease",
          }}
          title={isCurrentUser ? "No podés modificar tus propios permisos" : ""}
        >
          {row.can_edit_tariffs && (
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 14, marginRight: 4 }}
            >
              check
            </span>
          )}
          Gestión
        </span>
      </div>

      {/* Eliminar */}
      {!isCurrentUser && canEdit && (
        <Button size="sm" color="danger" outline onClick={onRemove} disabled={busy}>
          <span className="material-symbols-rounded" style={{ fontSize: 16 }}>
            delete
          </span>
        </Button>
      )}
      {isCurrentUser && (
        <span className="text-muted small" style={{ fontStyle: "italic" }}>
          No podés modificar tu propio acceso
        </span>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentCompany: state.companies.currentCompany,
  canEditPermissions: state.data.canEditTariffs,
});

export default connect(mapStateToProps)(AccesosMonitor);
